import { useEffect, useState } from "react";
import type { ComponentProps, FocusEvent, ReactNode } from "react";
import Creatable from "react-select/creatable";
import type { CreatableProps } from "react-select/creatable";
import { components } from "react-select";
import type {
  ClearIndicatorProps,
  ControlProps,
  GroupBase,
  IndicatorsContainerProps,
  OnChangeValue,
  PropsValue
} from "react-select";
import { FormattedMessage, useIntl } from "react-intl";
import { fromJS } from "immutable";
import isEqual from "lodash/fp/isEqual";

import { customStyles, getTheme, RemoveIcon } from "../up-styled-react-select";
import UpCustomerSettingsInfo from "../up-customer-settings-info";
import FieldsHOC from "../fields-hoc";
import type { FieldHocProps } from "../fields-hoc";
import { Icon } from "./styles";

export interface CreatableOption {
  className?: string;
  label: string;
  value: string;
}

type CreatableGroup = GroupBase<CreatableOption>;
type FormattedMessageProps = ComponentProps<typeof FormattedMessage>;
type TooltipMessage = Pick<
  FormattedMessageProps,
  "defaultMessage" | "id" | "values"
>;

interface LegacyCreatableProps {
  error?: boolean | string;
  infoInputText?: TooltipMessage;
  warningIndicator?: readonly string[];
}

export type UpCreatableProps<IsMulti extends boolean = false> = Omit<
  CreatableProps<CreatableOption, IsMulti, CreatableGroup>,
  "onChange"
> &
  LegacyCreatableProps & {
    onChange: NonNullable<
      CreatableProps<CreatableOption, IsMulti, CreatableGroup>["onChange"]
    >;
  };

const CONJUNCTION_STATES: Partial<Record<string, string>> = {
  en: "and",
  de: "und"
};

interface InfoProps {
  value: PropsValue<CreatableOption> | undefined;
  message: ReactNode;
}

const Info = ({ value, message }: InfoProps) => (
  <UpCustomerSettingsInfo
    infoText={message}
    icon="info-circle"
    margin={Array.isArray(value) && value.length > 0 ? "8px 0" : "8px"}
    width="200px"
  />
);

const isTooltipMessage = (value: unknown): value is TooltipMessage => {
  if (typeof value !== "object" || value === null) return false;
  if ("id" in value && value.id !== undefined && typeof value.id !== "string") {
    return false;
  }
  return !(
    "defaultMessage" in value &&
    value.defaultMessage !== undefined &&
    typeof value.defaultMessage !== "string" &&
    !Array.isArray(value.defaultMessage)
  );
};

const getWarningIndicator = (
  selectProps: object
): readonly string[] | undefined => {
  if (
    !("warningIndicator" in selectProps) ||
    !Array.isArray(selectProps.warningIndicator)
  ) {
    return undefined;
  }
  return selectProps.warningIndicator.filter(
    (item): item is string => typeof item === "string"
  );
};

const getInfoInputText = (
  selectProps: object
): TooltipMessage | undefined => {
  if (!("infoInputText" in selectProps)) return undefined;
  return isTooltipMessage(selectProps.infoInputText)
    ? selectProps.infoInputText
    : undefined;
};

const IndicatorsContainer = <IsMulti extends boolean>(
  props: IndicatorsContainerProps<CreatableOption, IsMulti, CreatableGroup>
) => {
  const { children, hasValue, selectProps } = props;
  const intl = useIntl();
  const warningIndicator = getWarningIndicator(selectProps);
  const infoInputText = getInfoInputText(selectProps);

  const parseWarningIntoString = (warnings: readonly string[]): string => {
    if (warnings.length === 1) return `"${warnings[0]}"`;

    const firsts = warnings.slice(0, warnings.length - 1);
    const last = warnings[warnings.length - 1];
    const conjunction = CONJUNCTION_STATES[intl.locale ?? "en"];

    return `${firsts
      .map((item) => `"${item}"`)
      .join(", ")} ${conjunction} "${last}"`;
  };

  const tooltipText: TooltipMessage | undefined =
    warningIndicator && warningIndicator.length > 0
      ? {
          id: "components.UpStyledCreatable.infoText",
          defaultMessage:
            "These selected filters {arr} contain a space at the beginning or end of the string. Please make sure they were added on purpose.",
          values: {
            arr: parseWarningIntoString(warningIndicator)
          }
        }
      : infoInputText;

  const tooltipNode =
    hasValue && tooltipText ? (
      <FormattedMessage {...tooltipText}>
        {(message) => (
          <Info value={selectProps.value} message={message} />
        )}
      </FormattedMessage>
    ) : undefined;

  return (
    <components.IndicatorsContainer {...props}>
      {tooltipNode}
      {children}
    </components.IndicatorsContainer>
  );
};

const hasSelectError = (selectProps: object): boolean =>
  "error" in selectProps && Boolean(selectProps.error);

const Control = <IsMulti extends boolean>(
  props: ControlProps<CreatableOption, IsMulti, CreatableGroup>
) => {
  const { children, selectProps } = props;
  const errorNode = hasSelectError(selectProps) ? (
    <Icon icon="exclamation-circle" />
  ) : undefined;

  return (
    <components.Control {...props}>
      {children}
      {errorNode}
    </components.Control>
  );
};

const CreatableClearIndicator = <IsMulti extends boolean>(
  props: ClearIndicatorProps<CreatableOption, IsMulti, CreatableGroup>
) => (
  <components.ClearIndicator {...props}>
    <RemoveIcon icon="times" />
  </components.ClearIndicator>
);

export const UpCreatable = <IsMulti extends boolean = false>(
  props: UpCreatableProps<IsMulti>
) => {
  const [warning, setWarning] = useState<readonly string[] | undefined>([]);

  useEffect(() => {
    if (props.isMulti) {
      const filteredValue = Array.isArray(props.value)
        ? props.value
            .filter((option) => !/^[^\s]+(\s+[^\s]+)*$/.test(option.value))
            .map((option) => option.value)
        : undefined;

      if (!isEqual(filteredValue, warning)) {
        setWarning(filteredValue);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.value, props.isMulti]);

  const creatableProps = { warningIndicator: warning, ...props };

  return (
    <Creatable<CreatableOption, IsMulti, CreatableGroup>
      styles={customStyles}
      components={{
        IndicatorsContainer,
        ClearIndicator: CreatableClearIndicator,
        Control
      }}
      theme={getTheme}
      {...creatableProps}
    />
  );
};

interface ImmutableCreatableValue {
  includes(value: unknown): boolean;
  push(value: unknown): ImmutableCreatableValue;
  toJS(): CreatableOption[];
}

interface CreatableFieldInput {
  name: string;
  value?: ImmutableCreatableValue;
  onChange(value: unknown): void;
}

type CreatableFieldProps = Omit<FieldHocProps, "input"> & {
  input: CreatableFieldInput;
};

const UnwrappedCreatable = ({ input, meta }: CreatableFieldProps) => {
  const intl = useIntl();

  const onChange = (
    selectedValue: OnChangeValue<CreatableOption, true> | null
  ): void => {
    input.onChange(fromJS(selectedValue ?? []));
  };

  const onBlur = (event: FocusEvent<HTMLInputElement>): void => {
    const typedValue = event.target.value;

    if (typedValue === "") return;

    const currentValue = input.value;
    if (currentValue === undefined) {
      throw new TypeError(
        "Cannot read properties of undefined (reading 'includes')"
      );
    }

    if (
      !currentValue.includes(
        fromJS({ label: typedValue, value: typedValue })
      ) &&
      !currentValue.includes(
        fromJS({
          label: typedValue,
          value: typedValue,
          className: "Select-create-option-placeholder"
        })
      )
    ) {
      input.onChange(
        currentValue.push(
          fromJS({ label: typedValue, value: typedValue })
        )
      );
    }
  };

  return (
    <UpCreatable
      isMulti
      value={input.value ? input.value.toJS() : []}
      name={input.name}
      onChange={onChange}
      onBlur={onBlur}
      options={[]}
      noOptionsMessage={() =>
        intl.formatMessage({
          id: "components.UpStyledCreatable.addFilterTag",
          defaultMessage: "Add a filter tag"
        })
      }
      placeholder={intl.formatMessage({
        id: "components.UpStyledCreatable.placeholder",
        defaultMessage: "Filters"
      })}
      formatCreateLabel={(label) =>
        intl.formatMessage(
          {
            id: "components.UpStyledCreatable.textCreator",
            defaultMessage: "Add filter tag {filter}"
          },
          { filter: label }
        )
      }
      error={Boolean(meta.error && meta.submitFailed)}
    />
  );
};

const creatable = FieldsHOC(UnwrappedCreatable);

export { creatable };
